/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppDispatch } from '../../../store/hooks';
import { fetchPermissions } from '../../../features/auth/authSlice';
import { Dropdown } from 'sharedUi/Dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'sharedUi/Button';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Toast } from 'sharedUi/Toast';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { sortRows } from '../../../utils/client-sort';
import { rolesApi, permissionSetsApi } from '../../../services/rbac.service';
import { projectsService, type AccessibleProject } from '../../../services/projects.service';
import type {
  Role,
  RoleCreateRequest,
  RoleUpdateRequest,
  RbacStatus,
} from '../../../types/rbac';
import SelectionPanel from '../../../components/rbac/selection-panel';
import DependencyDeleteDialog from '../../../components/rbac/dependency-delete-dialog';
import EntityLinksDialog, { LinkCard, LinkChips } from '../../../components/rbac/entity-links-dialog';
import ProjectCell from '../../../components/common/project-cell';
import SaasDataTable, {
  type SaasColumn,
  buildDefaultFilters,
  buildColumnFiltersParam,
} from '../../../components/common/saas-data-table';
import {
  StatusTag,
  CountBadge,
  useRbacToast,
  Field,
  TextField,
  TextAreaField,
  DetailShell,
  DetailCard,
  DetailRow,
  Chips,
  FormShell,
} from './shared';
import { useHasPermission } from '../../../hooks/usePermissions';
import { PERMISSIONS } from '../../../constants/permissions';

// Filterable fields — used to seed the default filter state.
const FILTER_DEFS: SaasColumn[] = [{ field: 'displayName', header: 'Display Name', filter: true }];

interface FormState {
  key: string;
  displayName: string;
  description: string;
  status: RbacStatus;
  permissionSetIds: string[];
  appId: string;
}

const EMPTY: FormState = {
  key: '',
  displayName: '',
  description: '',
  status: 'ACTIVE',
  permissionSetIds: [],
  appId: '',
};

const RolesTab = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const { toast, showSuccess, showError } = useRbacToast();
  const canCreate = useHasPermission(PERMISSIONS.AUTH_ROLE_CREATE);
  const canUpdate = useHasPermission(PERMISSIONS.AUTH_ROLE_UPDATE);
  const canDelete = useHasPermission(PERMISSIONS.AUTH_ROLE_DELETE);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // In-page (client-side) sort of the currently-loaded page. Search + filters stay server-side.
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  // "Show global/core rows" toggle — off by default: only the caller's own project.
  const [showGlobal, setShowGlobal] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewRow, setViewRow] = useState<Role | null>(null);
  const [linksRow, setLinksRow] = useState<Role | null>(null);

  const page = Math.floor(first / pageSize) + 1;
  const filtersParam = buildColumnFiltersParam(filters);
  const appIdParam = selectedProjectId || undefined;

  // Projects the caller may access — drives the project filter + form dropdown.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  // Lock the project on create when the caller can access exactly one project.
  const onlyProjectId = accessibleProjects.length === 1 ? accessibleProjects[0]._id : '';

  const { data, isFetching } = useQuery({
    queryKey: ['rbac', 'roles', debouncedSearch, page, pageSize, filtersParam, appIdParam, showGlobal],
    queryFn: () =>
      rolesApi.list({
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
        filters: filtersParam,
        app_id: appIdParam,
        include_global: showGlobal,
      }),
  });

  const handleFilter = (event: any) => {
    setFilters(event.filters || {});
    setFirst(0);
  };

  // All permission sets — used ONLY for list-view resolution (key<->id, resolving an
  // existing role's set keys to permissions). Not the picker: the picker is scoped.
  const { data: allSets = [] } = useQuery({
    queryKey: ['rbac', 'permission-sets', 'all'],
    queryFn: () => permissionSetsApi.listAll(),
  });

  // Picker options — scoped to the project chosen on the form (+ global). A role in
  // project X may only be offered project-X / global permission sets. Loaded while
  // the dialog is open; re-fetches when the selected project changes.
  const pickerAppId = form.appId || undefined;
  const { data: pickerSets = [] } = useQuery({
    queryKey: ['rbac', 'permission-sets', 'picker', pickerAppId ?? 'global'],
    queryFn: () => permissionSetsApi.listAll(undefined, { appId: pickerAppId, includeGlobal: true }),
    enabled: dialogOpen,
  });

  const setKeyToId = useMemo(() => {
    const m = new Map<string, string>();
    allSets.forEach((s) => m.set(s.key, s._id));
    return m;
  }, [allSets]);

  const setIdToSet = useMemo(() => {
    const m = new Map<string, (typeof allSets)[number]>();
    // Merge global list + scoped picker so both an existing role's preloaded ids and
    // freshly picked ids resolve for the permission preview.
    [...allSets, ...pickerSets].forEach((s) => m.set(s._id, s));
    return m;
  }, [allSets, pickerSets]);

  // Sets keyed by KEY — roles store defaultPermissionSets as set keys.
  const setByKey = useMemo(() => {
    const m = new Map<string, (typeof allSets)[number]>();
    allSets.forEach((s) => m.set(s.key, s));
    return m;
  }, [allSets]);

  // Resolved permissions for an existing role row (union of its sets' permissionKeys).
  const resolvedForRole = (row: Role): string[] => {
    const acc = new Set<string>();
    (row.defaultPermissionSets || []).forEach((k) => {
      setByKey.get(k)?.permissionKeys.forEach((pk) => acc.add(pk));
    });
    return Array.from(acc).sort();
  };

  // Picker options come from the project-scoped list, not the global one.
  const setOptions = useMemo(
    () => pickerSets.map((s) => ({ id: s._id, label: s.displayName || s.key, meta: s.key })),
    [pickerSets]
  );

  // Resolved permissions = union of selected sets' permissionKeys.
  const resolvedPermissionKeys = useMemo(() => {
    const acc = new Set<string>();
    form.permissionSetIds.forEach((id) => {
      const s = setIdToSet.get(id);
      (s?.permissionKeys || []).forEach((k) => acc.add(k));
    });
    return Array.from(acc).sort();
  }, [form.permissionSetIds, setIdToSet]);

  const rows = data?.data || [];
  const displayRows = useMemo(() => sortRows(rows, sortField, sortOrder), [rows, sortField, sortOrder]);
  const total = data?.pagination?.totalItems ?? (data?.pagination as any)?.total ?? 0;

  const setField = (k: keyof FormState, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY, appId: onlyProjectId });
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row: Role) => {
    setEditId(row._id);
    // Backend returns defaultPermissionSets as set KEYS; translate to set _ids.
    const ids = (row.defaultPermissionSets || [])
      .map((k) => setKeyToId.get(k))
      .filter((v): v is string => !!v);
    setForm({
      key: row.key,
      displayName: row.displayName || '',
      description: row.description || '',
      status: row.status || 'ACTIVE',
      permissionSetIds: ids,
      appId: row.appId || '',
    });
    setErrors({});
    setDialogOpen(true);
  };

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['rbac', 'roles'] });
    // A role's permission sets changed → refresh the current user's resolved perms.
    dispatch(fetchPermissions());
  };

  const createMut = useMutation({
    mutationFn: (payload: RoleCreateRequest) => rolesApi.create(payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Role created');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to create role'),
  });

  const updateMut = useMutation({
    mutationFn: (payload: RoleUpdateRequest) => rolesApi.update(editId!, payload),
    onSuccess: () => {
      invalidate();
      showSuccess('Role updated');
      setDialogOpen(false);
    },
    onError: (e) => showError(e, 'Failed to update role'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => rolesApi.remove(id),
    onSuccess: () => {
      invalidate();
      showSuccess('Role deleted');
      setDeleteId(null);
    },
    onError: (e) => {
      showError(e, 'Failed to delete role');
      setDeleteId(null);
    },
  });

  const saving = createMut.isPending || updateMut.isPending;

  const validate = () => {
    const err: Record<string, string> = {};
    if (!form.key.trim()) err.key = 'Key is required';
    // Backend requires a display name on create — validate it here for parity.
    if (!form.displayName.trim()) err.displayName = 'Display name is required';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const save = () => {
    if (!validate()) return;
    if (editId) {
      updateMut.mutate({
        displayName: form.displayName || undefined,
        description: form.description || undefined,
        defaultPermissionSetIds: form.permissionSetIds,
        status: form.status,
      });
    } else {
      createMut.mutate({
        key: form.key.trim(),
        displayName: form.displayName || undefined,
        description: form.description || undefined,
        defaultPermissionSetIds: form.permissionSetIds,
        status: form.status,
        appId: form.appId || undefined,
      });
    }
  };

  const columns: SaasColumn<Role>[] = useMemo(
    () => [
      { field: 'key', header: 'Key', sortable: true, filter: true, hideable: false, body: (r) => <span className="font-mono text-xs">{r.key}</span> },
      { field: 'displayName', header: 'Display Name', sortable: true, filter: true, body: (r) => r.displayName || '-' },
      {
        field: 'sets',
        header: 'Permission Sets',
        body: (r) => <CountBadge value={(r.defaultPermissionSets || []).length} />,
      },
      {
        field: 'appId',
        header: 'Project',
        body: (r) => <ProjectCell appId={r.appId} projects={accessibleProjects} />,
      },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        body: (r) => (
          <div className="flex justify-center gap-1">
            <button
              className="rounded p-2 transition-colors hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
              onClick={() => setViewRow(r)}
              aria-label="View"
            >
              <i className="pi pi-eye text-emerald-600" />
            </button>
            <button
              className="rounded p-2 transition-colors hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
              onClick={() => setLinksRow(r)}
              aria-label="Links"
              title="Linked entities"
            >
              <i className="pi pi-sitemap text-indigo-600" />
            </button>
            {canUpdate && (
              <button
                className="rounded p-2 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => openEdit(r)}
                aria-label="Edit"
              >
                <i className="pi pi-pencil text-blue-600" />
              </button>
            )}
            {canDelete && (
              <button
                className="rounded p-2 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20"
                onClick={() => setDeleteId(r._id)}
                aria-label="Delete"
              >
                <i className="pi pi-trash text-red-600" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [setKeyToId, accessibleProjects]
  );

  // Full-page detail view (replaces the old dialog).
  if (viewRow) {
    const sets = viewRow.defaultPermissionSets || [];
    const resolved = resolvedForRole(viewRow);
    const projName = accessibleProjects.find((p: AccessibleProject) => p._id === viewRow.appId)?.name || 'Global';
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <DetailShell
          backLabel="Back to Roles"
          onBack={() => setViewRow(null)}
          title={viewRow.displayName || viewRow.key}
          subtitle={`${viewRow.key} · ${projName}`}
          actions={canUpdate ? <Button label="Edit" icon="pi pi-pencil" size="small" onClick={() => { const r = viewRow; setViewRow(null); openEdit(r); }} /> : undefined}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <DetailCard title="Role">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <DetailRow label="Key" value={viewRow.key} mono />
                <DetailRow label="Status" value={<StatusTag status={viewRow.status} />} />
                <DetailRow label="Display name" value={viewRow.displayName || '—'} />
                <DetailRow label="Project" value={projName} />
                {viewRow.description && (
                  <div className="col-span-2"><DetailRow label="Description" value={viewRow.description} /></div>
                )}
              </div>
            </DetailCard>
            <DetailCard title="Audit">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {(viewRow as any).createdAt && <DetailRow label="Created" value={formatTimestamp((viewRow as any).createdAt)} />}
                {(viewRow as any).updatedAt && <DetailRow label="Updated" value={formatTimestamp((viewRow as any).updatedAt)} />}
                <div className="col-span-2"><DetailRow label="Role ID" value={viewRow._id} mono /></div>
              </div>
            </DetailCard>
          </div>
          <div className="mt-3 grid gap-3">
            <DetailCard title="Default permission sets" count={sets.length}>
              <Chips items={sets} tone="slate" />
            </DetailCard>
            <DetailCard title="Resolved permissions" count={resolved.length}>
              <div className="max-h-72 overflow-auto"><Chips items={resolved} tone="emerald" /></div>
            </DetailCard>
          </div>
        </DetailShell>
      </>
    );
  }

  // Full-page create / edit form (replaces the old dialog).
  if (dialogOpen) {
    return (
      <>
        <Toast ref={toast} position="top-right" />
        <FormShell
          backLabel="Back to Roles"
          onBack={() => !saving && setDialogOpen(false)}
          title={editId ? 'Edit Role' : 'Create Role'}
          onCancel={() => setDialogOpen(false)}
          onSave={save}
          saving={saving}
          isEdit={!!editId}
        >
          <div className="flex flex-col gap-3">
            {/* Project first — it scopes the permission sets offered below. */}
            <Field label="Project">
              <Dropdown
                value={form.appId || null}
                options={[
                  { label: 'Global (all projects)', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e) => setForm((p) => ({ ...p, appId: e.value ?? '', permissionSetIds: [] }))}
                placeholder="Select project"
                disabled={!!editId || accessibleProjects.length <= 1}
                className="w-full md:w-1/2"
              />
            </Field>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="Key" required error={errors.key}>
                <TextField value={form.key} onChange={(v) => setField('key', v)} placeholder="e.g. admin" disabled={!!editId} />
              </Field>
              <Field label="Display Name" required error={errors.displayName}>
                <TextField value={form.displayName} onChange={(v) => setField('displayName', v)} placeholder="Enter display name" />
              </Field>
            </div>

            <Field label="Description">
              <TextAreaField value={form.description} onChange={(v) => setField('description', v)} placeholder="Optional description" rows={2} />
            </Field>

            <SelectionPanel
              title="Permission Sets"
              helper={`${form.permissionSetIds.length} selected`}
              options={setOptions}
              selectedIds={form.permissionSetIds}
              onChange={(ids) => setField('permissionSetIds', ids)}
            />

            {/* Resolved permissions preview (read-only) */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Resolved permissions ({resolvedPermissionKeys.length})
              </label>
              <div className="max-h-32 overflow-auto rounded-md border border-line bg-canvas dark:bg-gray-900/40 p-2">
                {resolvedPermissionKeys.length === 0 ? (
                  <span className="text-xs text-ink-muted">
                    Select permission sets to preview their combined permissions.
                  </span>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {resolvedPermissionKeys.map((k) => (
                      <span key={k} className="rounded bg-gray-200 dark:bg-gray-700 px-2 py-0.5 font-mono text-[11px] text-gray-700 dark:text-gray-200">
                        {k}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

          </div>
        </FormShell>
      </>
    );
  }

  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <SaasDataTable
        tableId="rbac-roles"
        columns={columns}
        data={displayRows}
        loading={isFetching}
        dataKey="_id"
        emptyMessage="No roles found"
        first={first}
        rows={pageSize}
        totalRecords={total}
        onPage={(e: any) => {
          setFirst(e.first);
          setPageSize(e.rows);
        }}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sortField={sortField ?? undefined}
        sortOrder={sortOrder ?? undefined}
        onSort={(e: any) => {
          setSortField(e.sortField);
          setSortOrder(e.sortOrder);
        }}
        filters={filters}
        onFilter={handleFilter}
        globalSearch={search}
        onGlobalSearchChange={(v) => {
          setSearch(v);
          setFirst(0);
        }}
        globalSearchPlaceholder="Search roles..."
        testId="RolesList"
        toolbarStart={
          <div className="flex flex-wrap items-center gap-3">
            {accessibleProjects.length > 1 && (
              <Dropdown
                value={selectedProjectId}
                options={[
                  { label: 'All projects', value: null },
                  ...accessibleProjects.map((p: AccessibleProject) => ({
                    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    value: p._id,
                  })),
                ]}
                onChange={(e: any) => { setSelectedProjectId(e.value ?? null); setFirst(0); }}
                placeholder="All projects"
                style={{ minWidth: '190px' }}
              />
            )}
            <label className="flex items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
              <InputSwitch checked={showGlobal} onChange={(e: any) => { setShowGlobal(!!e.value); setFirst(0); }} />
              Show core
            </label>
          </div>
        }
        toolbarEnd={
          <>
            <span className="whitespace-nowrap text-sm text-ink-muted">
              {total} {total === 1 ? 'record' : 'records'}
            </span>
            <RefreshButton
              onClick={() => queryClient.invalidateQueries({ queryKey: ['rbac', 'roles'] })}
              loading={isFetching}
            />
            {canCreate && <Button label="Create Role" icon="pi pi-plus" onClick={openCreate} size="small" />}
          </>
        }
      />

      <DependencyDeleteDialog
        entityId={deleteId}
        entityLabel="role"
        resourceKey="role"
        getDependencies={rolesApi.getDependencies}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteMut.mutate(deleteId)}
        deleting={deleteMut.isPending}
      />

      <EntityLinksDialog
        entityId={linksRow?._id ?? null}
        title="Role Links"
        resourceKey="role"
        getDependencies={rolesApi.getDependencies}
        onHide={() => setLinksRow(null)}
      >
        <LinkCard
          icon="pi pi-clone"
          title="Permission Sets"
          count={(linksRow?.defaultPermissionSets || []).length}
        >
          <LinkChips items={linksRow?.defaultPermissionSets || []} tone="slate" />
        </LinkCard>
        {linksRow && (
          <LinkCard icon="pi pi-key" title="Resolved Permissions" count={resolvedForRole(linksRow).length}>
            <LinkChips items={resolvedForRole(linksRow)} tone="emerald" />
          </LinkCard>
        )}
      </EntityLinksDialog>
    </div>
  );
};

export default RolesTab;
